import { useEffect, useState, useRef, FormEvent } from "react";
import FormGroup from "../../../forms/FormGroup";
import { LabeledInputWithButton } from "../../../forms/LabeledInput";
import { useRole, useContract } from "../../../contract/ContractContext";
import { useInvitationStatus } from "../../../contract/invitation/InvitationStatus";
import { useRoleName } from "../../../contract/ContractContext";
import ErrorMessage from "../../../shared/error/ErrorMessage";
import { EMAIL_PATTERN } from "../../../contract/contact-info/ContactInfoSection";
import { EVENTS } from "../../../Tracking";
import { captureException } from "@sentry/core";
import { useContractClient } from "../../../api/ContractClient";
import { Role } from "../../../contract/model/ContractViewModel";
import { ButtonLink } from "@teamscheme/button-link";
import { ApiError } from "../../../api/ApiClient";

export default function ContinueLaterForm({ isOpen }: { isOpen: boolean }) {
  const emailInput = useRef<HTMLInputElement | null>(null);
  const role = useRole();
  const contract = useContract();
  const { shouldInvite, hasInvited } = useInvitationStatus();
  const contractingParty =
    role === Role.Seller ? contract.seller : contract.buyer;
  const { other } = useRoleName();
  const [email, setEmail] = useState(contractingParty?.email);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    if (isOpen && emailInput.current) emailInput.current.focus();
  }, [isOpen]);

  const client = useContractClient();

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    setIsSubmitting(true);

    try {
      setError(null);

      await client.post(`contract/${contract.id}/save`, {
        email,
      });

      setHasSaved(true);
    } catch (e) {
      const error = e as ApiError;
      setError(error);
      captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const isValid = email && new RegExp(EMAIL_PATTERN).test(email);

  const [showLink, setShowLink] = useState(true);
  const contractLinkInput = useRef<HTMLInputElement | null>(null);
  const contractLink = document.location.href;
  const defaultCopyButtonTitle = "Kopier";
  const [copyButtonTitle, setCopyButtonTitle] = useState(
    defaultCopyButtonTitle
  );

  useEffect(() => {
    const timeout = setTimeout(
      () => setCopyButtonTitle(defaultCopyButtonTitle),
      5000
    );

    return () => clearTimeout(timeout);
  }, [copyButtonTitle]);

  const x = (
    <p>
      {shouldInvite ? (
        hasInvited ? (
          <span>
            Du finner alltid igjen kontrakten ved å bruke den personlige lenken
            du fikk på SMS da du inviterte {other}.
          </span>
        ) : (
          <span>
            Når du inviterer {other} får du en bekreftelse på SMS med personlig
            lenke.
          </span>
        )
      ) : (
        <span>
          Du finner alltid igjen kontrakten ved å bruke den personlige lenken du
          fikk da du ble invitert av {other}.
        </span>
      )}
    </p>
  );

  if (showLink) {
    return (
      <form onSubmit={(event) => event.preventDefault()}>
        {x}
        <FormGroup>
          <LabeledInputWithButton
            label="Bruk denne lenken for å fortsette senere:"
            id="menu.contractLink"
            name="contractLink"
            inputRef={contractLinkInput}
            inputProps={{
              type: "url",
              value: contractLink,
              onChange: () => {},
            }}
            buttonProps={{
              children: copyButtonTitle,
              trackEvent: EVENTS.SAVE_CONTRACT_MODAL.COPY_LINK,
              onClick: () => {
                navigator.clipboard.writeText(contractLink);
                setCopyButtonTitle("Kopiert!");
              },
            }}
          />
        </FormGroup>
        <p>
          <ButtonLink variant="secondary" onClick={() => setShowLink(false)}>
            Eller få lenken tilsendt på e-post
          </ButtonLink>
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      {x}
      <FormGroup>
        <LabeledInputWithButton
          label="Send lenken til deg selv på e-post:"
          id="menu.contractLink.email"
          name="email"
          inputRef={emailInput}
          inputProps={{
            type: "email",
            value: email || "",
            disabled: hasSaved,
            placeholder: "Din e-postadresse",
            onChange: (event: any) => setEmail(event.target.value || null),
          }}
          buttonProps={{
            children: "Send",
            isLoading: isSubmitting,
            trackEvent: EVENTS.SAVE_CONTRACT_MODAL.SEND_LINK,
            disabled: !isValid || hasSaved,
            onClick: onSubmit,
          }}
        />
      </FormGroup>
      {hasSaved ? (
        <p>OK! Vi har sendt deg en e-post med lenke til kontrakten.</p>
      ) : null}
      {error ? (
        <ErrorMessage>En feil har oppstått! Prøv igjen.</ErrorMessage>
      ) : null}
      <p>
        <ButtonLink variant="secondary" onClick={() => setShowLink(true)}>
          Eller kopier lenken
        </ButtonLink>
      </p>
    </form>
  );
}
